import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";

export default function FixUnconfirmedUsers() {
  const [isFixing, setIsFixing] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleFixUsers = async () => {
    setIsFixing(true);
    setResult(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.access_token) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke('fix-unconfirmed-users', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      setResult(data);

      if (data.success) {
        toast({
          title: "Users Fixed",
          description: data.message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: data.error || "Failed to fix users",
        });
      }
    } catch (error: any) {
      console.error("Error fixing users:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to fix users",
      });
      setResult({ success: false, error: error.message });
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <div className="container max-w-4xl py-6">
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle>Fix Unconfirmed Users</CardTitle>
          <CardDescription>
            This tool will confirm all users who have unconfirmed emails in the system.
            Use this if organization users are unable to login due to email confirmation issues.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This action will confirm all users with unconfirmed emails. Make sure email confirmation
              is disabled in your Supabase Auth settings (Authentication → Providers → Email → Turn OFF "Confirm email").
            </AlertDescription>
          </Alert>

          <Button 
            onClick={handleFixUsers} 
            disabled={isFixing}
            className="w-full"
          >
            {isFixing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isFixing ? "Fixing Users..." : "Fix Unconfirmed Users"}
          </Button>

          {result && (
            <Alert variant={result.success ? "default" : "destructive"}>
              {result.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">{result.message || result.error}</p>
                  {result.success && (
                    <div className="text-sm space-y-1">
                      <p>Total Users: {result.totalUsers}</p>
                      <p>Fixed: {result.fixedCount}</p>
                      <p>Already Confirmed: {result.alreadyConfirmedCount}</p>
                      {result.errorCount > 0 && (
                        <p className="text-destructive">Errors: {result.errorCount}</p>
                      )}
                    </div>
                  )}
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="font-semibold text-sm">Errors:</p>
                      <ul className="text-sm list-disc list-inside">
                        {result.errors.map((err: string, idx: number) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
